/**
 * The family map. Mapbox GL JS (v3) — Brian's standing map choice, not a substitute.
 *
 * Two things worth knowing:
 *
 * 1. `mapbox-gl` is not in package.json (this module is not allowed to add a
 *    dependency), so the library is loaded on demand from Mapbox's own CDN — the
 *    same origin the tiles come from anyway — and only when a token exists. If
 *    the integrator adds `mapbox-gl` to package.json, swap `loadMapbox()` for a
 *    static import and delete nothing else.
 * 2. Without VITE_MAPBOX_TOKEN there is no map, and the page shows the card list
 *    instead. That is not a degraded experience on a phone — it is the better one.
 *
 * Controls: a style switcher (Streets / Satellite / Outdoors / Dark / 3D), a
 * recenter button that reframes everyone, and — below the map — a row of family
 * avatars that fly the camera to a person on tap. The avatar markers are HTML
 * overlays, so they survive a `setStyle` and never need redrawing on a style change.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MemberLocation, MemberSummary } from '../../shared/types';
import { clockTime, coordLabel, initialsOf, relativeTime } from './format';

const MAPBOX_VERSION = 'v3.9.0';
const MAPBOX_JS = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_VERSION}/mapbox-gl.js`;
const MAPBOX_CSS = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_VERSION}/mapbox-gl.css`;

/**
 * The switchable views. `threeD` turns on a pitched camera + terrain + the Standard
 * style's 3D buildings; everything else is a flat top-down basemap. `dark` lets the
 * switcher UI flip its own contrast so the active chip stays legible on the tiles.
 */
interface StyleDef {
  id: string;
  label: string;
  url: string;
  threeD?: boolean;
}
const STYLES: StyleDef[] = [
  { id: 'streets', label: 'Streets', url: 'mapbox://styles/mapbox/streets-v12' },
  { id: 'satellite', label: 'Satellite', url: 'mapbox://styles/mapbox/satellite-streets-v12' },
  { id: 'outdoors', label: 'Outdoors', url: 'mapbox://styles/mapbox/outdoors-v12' },
  { id: 'dark', label: 'Dark', url: 'mapbox://styles/mapbox/dark-v11' },
  { id: '3d', label: '3D', url: 'mapbox://styles/mapbox/standard', threeD: true },
];
const DEFAULT_STYLE = 'streets';

// Minimal structural types for the bits of the Mapbox API we actually call.
type LngLat = [number, number];
interface CameraOptions {
  pitch?: number;
  bearing?: number;
  duration?: number;
  center?: LngLat;
  zoom?: number;
  essential?: boolean;
  padding?: number;
  maxZoom?: number;
}
interface MapboxMap {
  addControl(control: unknown, position?: string): void;
  fitBounds(bounds: unknown, options: CameraOptions): void;
  flyTo(options: CameraOptions): void;
  easeTo(options: CameraOptions): void;
  setStyle(style: string): void;
  getPitch(): number;
  getBearing(): number;
  addSource(id: string, source: Record<string, unknown>): void;
  getSource(id: string): unknown;
  setTerrain(terrain: Record<string, unknown> | null): void;
  resize(): void;
  remove(): void;
  on(event: string, handler: () => void): void;
  once(event: string, handler: () => void): void;
}
interface MapboxMarker {
  setLngLat(c: LngLat): MapboxMarker;
  setPopup(popup: unknown): MapboxMarker;
  addTo(map: MapboxMap): MapboxMarker;
  remove(): void;
}
interface MapboxBounds {
  extend(c: LngLat): MapboxBounds;
}
interface MapboxGL {
  accessToken: string;
  Map: new (options: Record<string, unknown>) => MapboxMap;
  Marker: new (options?: Record<string, unknown>) => MapboxMarker;
  Popup: new (options?: Record<string, unknown>) => { setHTML(html: string): unknown };
  NavigationControl: new (options?: Record<string, unknown>) => unknown;
  LngLatBounds: new () => MapboxBounds;
}
declare global {
  interface Window {
    mapboxgl?: MapboxGL;
  }
}

let loader: Promise<MapboxGL> | null = null;

function loadMapbox(): Promise<MapboxGL> {
  if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
  if (loader) return loader;

  loader = new Promise<MapboxGL>((resolve, reject) => {
    if (!document.querySelector(`link[href="${MAPBOX_CSS}"]`)) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = MAPBOX_CSS;
      document.head.appendChild(css);
    }
    const script = document.createElement('script');
    script.src = MAPBOX_JS;
    script.async = true;
    script.onload = () =>
      window.mapboxgl
        ? resolve(window.mapboxgl)
        : reject(new Error('Mapbox loaded but did not register'));
    script.onerror = () => {
      loader = null;
      reject(new Error('Could not load Mapbox GL'));
    };
    document.head.appendChild(script);
  });

  return loader;
}

/** Place names can be third-party strings — never inject them raw. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function markerElement(
  member: MemberSummary & { avatar_url?: string | null },
  driving: boolean
): HTMLElement {
  const el = document.createElement('div');
  el.setAttribute('role', 'img');
  el.setAttribute('aria-label', `${member.name}${driving ? ', driving' : ''}`);
  el.style.cssText = [
    'position:relative',
    'width:44px',
    'height:44px',
    'border-radius:9999px',
    'display:grid',
    'place-items:center',
    `background:${member.color}`,
    'color:#fff',
    'font:600 15px/1 Inter var, system-ui, sans-serif',
    'border:3px solid #fff',
    'box-shadow:0 2px 8px rgba(43,39,35,.28)',
    'cursor:pointer',
  ].join(';');

  // Initials sit underneath as the fallback; the photo (when there is one) overlays
  // them and, on a load error, removes itself so the initials show through.
  const initials = document.createElement('span');
  initials.textContent = initialsOf(member.name);
  el.appendChild(initials);

  if (member.avatar_url) {
    const img = document.createElement('img');
    img.src = member.avatar_url;
    img.alt = '';
    img.style.cssText =
      'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border-radius:9999px';
    img.onerror = () => img.remove();
    el.appendChild(img);
  }

  if (driving) {
    const badge = document.createElement('span');
    badge.textContent = '🚗';
    badge.setAttribute('aria-hidden', 'true');
    badge.style.cssText =
      'position:absolute;bottom:-2px;right:-6px;font-size:14px;background:#fff;border-radius:9999px;padding:1px 3px;box-shadow:0 1px 3px rgba(43,39,35,.3);z-index:1';
    el.appendChild(badge);
  }
  return el;
}

interface Props {
  locations: MemberLocation[];
  members: (MemberSummary & { avatar_url?: string | null })[];
  token: string;
  /** Called when Mapbox itself can't load — the page falls back to cards only. */
  onUnavailable: () => void;
}

export default function FamilyMap({ locations, members, token, onUnavailable }: Props) {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<MapboxMap | null>(null);
  const gl = useRef<MapboxGL | null>(null);
  const markers = useRef<MapboxMarker[]>([]);
  const [ready, setReady] = useState(false);
  const [styleId, setStyleId] = useState(DEFAULT_STYLE);
  // The map is created WITH the default style, so the first style effect run is a
  // no-op we skip — otherwise it reloads the same style and flattens the camera.
  const styleInit = useRef(false);

  const onUnavailableRef = useRef(onUnavailable);
  useEffect(() => {
    onUnavailableRef.current = onUnavailable;
  }, [onUnavailable]);

  /** Frame everyone who has a fix. Preserves the current pitch/bearing so a 3D or
      rotated view isn't yanked flat when positions refresh or you tap Recenter. */
  const frameAll = useCallback(() => {
    const mapboxgl = gl.current;
    const instance = map.current;
    if (!mapboxgl || !instance) return;
    const bounds = new mapboxgl.LngLatBounds();
    let count = 0;
    for (const loc of locations) {
      bounds.extend([loc.lng, loc.lat]);
      count++;
    }
    if (count === 0) return;
    instance.fitBounds(bounds, {
      padding: 72,
      maxZoom: 14,
      duration: 600,
      pitch: instance.getPitch(),
      bearing: instance.getBearing(),
    });
  }, [locations]);

  const flyToMember = useCallback((loc: MemberLocation) => {
    const instance = map.current;
    if (!instance) return;
    instance.flyTo({ center: [loc.lng, loc.lat], zoom: 15.5, duration: 1200, essential: true });
  }, []);

  // Create the map once (per token).
  useEffect(() => {
    let cancelled = false;

    loadMapbox()
      .then((mapboxgl) => {
        if (cancelled || !container.current || map.current) return;
        mapboxgl.accessToken = token;
        gl.current = mapboxgl;
        const initial = STYLES.find((s) => s.id === DEFAULT_STYLE) ?? STYLES[0];
        const instance = new mapboxgl.Map({
          container: container.current,
          style: initial.url,
          center: [-80.8431, 35.2271], // Charlotte — replaced by fitBounds as soon as we have a fix
          zoom: 9,
          attributionControl: true,
        });
        instance.addControl(new mapboxgl.NavigationControl({ showCompass: true }), 'top-right');
        instance.on('load', () => {
          if (!cancelled) setReady(true);
        });
        map.current = instance;
      })
      .catch((err) => {
        console.error('mapbox: unavailable, falling back to the list view', err);
        if (!cancelled) onUnavailableRef.current();
      });

    return () => {
      cancelled = true;
      for (const m of markers.current) m.remove();
      markers.current = [];
      map.current?.remove();
      map.current = null;
      gl.current = null;
      styleInit.current = false; // a fresh map re-shows the default style; skip its first run too
      setReady(false);
    };
  }, [token]);

  // Switch basemap (and 3D terrain/pitch) when the chosen style changes.
  useEffect(() => {
    const instance = map.current;
    if (!ready || !instance) return;
    if (!styleInit.current) {
      styleInit.current = true; // map already shows DEFAULT_STYLE; don't reload it
      return;
    }
    const def = STYLES.find((s) => s.id === styleId) ?? STYLES[0];
    instance.setStyle(def.url);
    // setStyle wipes sources/terrain; re-apply once the new style is loaded.
    instance.once('style.load', () => {
      if (map.current !== instance) return;
      if (def.threeD) {
        if (!instance.getSource('mapbox-dem')) {
          instance.addSource('mapbox-dem', {
            type: 'raster-dem',
            url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
            tileSize: 512,
            maxzoom: 14,
          });
        }
        instance.setTerrain({ source: 'mapbox-dem', exaggeration: 1.3 });
        instance.easeTo({ pitch: 55, duration: 700 });
      } else {
        instance.setTerrain(null);
        instance.easeTo({ pitch: 0, bearing: 0, duration: 500 });
      }
    });
    // styleId is the only real trigger; `ready` gates the first run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleId, ready]);

  // Re-draw markers whenever positions change, then frame everyone.
  useEffect(() => {
    const mapboxgl = gl.current;
    const instance = map.current;
    if (!ready || !mapboxgl || !instance) return;

    for (const m of markers.current) m.remove();
    markers.current = [];

    for (const loc of locations) {
      const member = members.find((m) => m.id === loc.member_id);
      if (!member) continue;

      const place = loc.place_name ?? coordLabel(loc.lat, loc.lng);
      const popup = new mapboxgl.Popup({ offset: 28, closeButton: false }).setHTML(
        `<div style="font:500 14px/1.4 Inter var,system-ui,sans-serif;color:#2b2723">
           <strong>${escapeHtml(member.name)}</strong><br />
           ${escapeHtml(place)}<br />
           <span style="color:#6b6259">${escapeHtml(
             `${relativeTime(loc.recorded_at)} · ${clockTime(loc.recorded_at)}`
           )}</span>
         </div>`
      );

      const marker = new mapboxgl.Marker({ element: markerElement(member, loc.is_driving) })
        .setLngLat([loc.lng, loc.lat])
        .setPopup(popup)
        .addTo(instance);

      markers.current.push(marker);
    }

    frameAll();
  }, [ready, locations, members, frameAll]);

  // People with a live fix — the fly-to row and the recenter target.
  const placed = locations
    .map((loc) => ({ loc, member: members.find((m) => m.id === loc.member_id) }))
    .filter((x): x is { loc: MemberLocation; member: MemberSummary } => Boolean(x.member));

  const dark = styleId === 'dark' || styleId === 'satellite';

  return (
    <div className="space-y-3">
      <div className="relative">
        <div
          ref={container}
          className="h-72 w-full overflow-hidden rounded-card border border-border bg-surface-sunk sm:h-96"
          aria-label="Map of where everyone is"
          role="region"
        />

        {/* Style switcher — top-left, over the map. */}
        <div
          className={`absolute left-2 top-2 flex gap-1 rounded-full border p-1 shadow-sm backdrop-blur ${
            dark ? 'border-white/20 bg-black/45' : 'border-border bg-surface/85'
          }`}
          role="group"
          aria-label="Map style"
        >
          {STYLES.map((s) => {
            const active = s.id === styleId;
            return (
              <button
                key={s.id}
                type="button"
                aria-pressed={active}
                onClick={() => setStyleId(s.id)}
                className={`min-h-8 rounded-full px-2.5 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-sage ${
                  active
                    ? 'bg-sage text-white'
                    : dark
                      ? 'text-white/85 hover:bg-white/15'
                      : 'text-ink-soft hover:bg-surface-sunk hover:text-ink'
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        {/* Recenter — bottom-left, over the map. */}
        <button
          type="button"
          onClick={frameAll}
          disabled={placed.length === 0}
          className={`absolute bottom-2 left-2 inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-medium shadow-sm backdrop-blur transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-sage disabled:opacity-50 ${
            dark
              ? 'border-white/20 bg-black/45 text-white hover:bg-black/60'
              : 'border-border bg-surface/85 text-ink hover:bg-surface'
          }`}
        >
          <RecenterIcon /> Recenter
        </button>
      </div>

      {/* Tap a person to fly to them. */}
      {placed.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1" aria-label="Jump to a person on the map">
          {placed.map(({ loc, member }) => (
            <button
              key={member.id}
              type="button"
              onClick={() => flyToMember(loc)}
              className="inline-flex shrink-0 items-center gap-2 rounded-full border border-border bg-surface py-1 pl-1 pr-3 text-sm font-medium text-ink hover:bg-surface-sunk focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            >
              <span
                aria-hidden="true"
                className="grid h-7 w-7 place-items-center rounded-full text-xs font-semibold text-white"
                style={{ backgroundColor: member.color }}
              >
                {initialsOf(member.name)}
              </span>
              {member.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RecenterIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </svg>
  );
}
